import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { ZERO_GUID } from 'modules/enumerations/enumerationIds';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  infoText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

const ANY = '';

const titleOf = (list, id) => (list || []).find(x => isIdEqual(x.id, id))?.title;
const hasValue = id => !!id && !isIdEqual(id, ZERO_GUID);
const byTitle = (a, b) => a.title.localeCompare(b.title);

// Picks the letter a letter-sent rule fires on. Service and Regarding only narrow the letter list (they are built from
// the letters themselves, so every choice has letters); the rule stores just the letter.
const LetterTriggerSection = ({
  letterOptions,
  serviceEnum,
  regardingEnum,
  selectedRootTemplateId,
  cutoffDate,
  onLetterChange,
  onCutoffChange,
  error,
  isLoading,
}) => {
  const classes = useStyles();
  const [serviceFilter, setServiceFilter] = useState(ANY);
  const [regardingFilter, setRegardingFilter] = useState(ANY);

  const letters = useMemo(() => (
    letterOptions
      .map(option => ({
        ...option,
        serviceTitle: titleOf(serviceEnum, option.serviceId) || 'No service',
        regardingTitle: titleOf(regardingEnum, option.regardingId) || '',
      }))
      .sort((a, b) => a.serviceTitle.localeCompare(b.serviceTitle) || a.name.localeCompare(b.name))
  ), [letterOptions, serviceEnum, regardingEnum]);

  const selectedLetter = letters.find(l => String(l.rootTemplateId) === String(selectedRootTemplateId)) || null;

  // A saved rule opens with the filters set to its letter's service
  const hasSyncedFilters = useRef(false);
  useEffect(() => {
    if (!hasSyncedFilters.current && selectedLetter) {
      hasSyncedFilters.current = true;
      setServiceFilter(hasValue(selectedLetter.serviceId) ? selectedLetter.serviceId : ANY);
    }
  }, [selectedLetter]);

  const serviceOptions = useMemo(() => {
    const options = [];
    letters.forEach((l) => {
      if (hasValue(l.serviceId) && !options.some(o => isIdEqual(o.id, l.serviceId))) {
        options.push({ id: l.serviceId, title: l.serviceTitle });
      }
    });
    return options.sort(byTitle);
  }, [letters]);

  const matchesService = l => serviceFilter === ANY || isIdEqual(l.serviceId, serviceFilter);
  const matchesRegarding = l => regardingFilter === ANY || isIdEqual(l.regardingId, regardingFilter);

  const regardingOptions = useMemo(() => {
    const options = [];
    letters
      .filter(l => serviceFilter === ANY || isIdEqual(l.serviceId, serviceFilter))
      .forEach((l) => {
        if (hasValue(l.regardingId) && l.regardingTitle && !options.some(o => isIdEqual(o.id, l.regardingId))) {
          options.push({ id: l.regardingId, title: l.regardingTitle });
        }
      });
    return options.sort(byTitle);
  }, [letters, serviceFilter]);

  const filteredLetters = letters.filter(l => matchesService(l) && matchesRegarding(l));

  const handleServiceFilterChange = (value) => {
    setServiceFilter(value);
    setRegardingFilter(ANY);
    if (selectedLetter && value !== ANY && !isIdEqual(selectedLetter.serviceId, value)) {
      onLetterChange(null);
    }
  };

  const handleRegardingFilterChange = (value) => {
    setRegardingFilter(value);
    if (selectedLetter && value !== ANY && !isIdEqual(selectedLetter.regardingId, value)) {
      onLetterChange(null);
    }
  };

  return (
    <Paper className={classes.container}>
      <Typography variant="h6" gutterBottom>
        Letter Trigger
      </Typography>
      <Typography variant="body2" className={classes.infoText}>
        A task is created once for every send of the selected letter on an open authorization, re-sends included.
        Narrow the list by Service and Regarding, then pick the letter.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Letter Service</InputLabel>
            <Select value={serviceFilter} onChange={(e) => handleServiceFilterChange(e.target.value)} disabled={isLoading}>
              <MenuItem value={ANY}>
                <em>Any service</em>
              </MenuItem>
              {serviceOptions.map(option => (
                <MenuItem key={option.id} value={option.id}>
                  {option.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Letter Regarding</InputLabel>
            <Select value={regardingFilter} onChange={(e) => handleRegardingFilterChange(e.target.value)} disabled={isLoading}>
              <MenuItem value={ANY}>
                <em>Any regarding</em>
              </MenuItem>
              {regardingOptions.map(option => (
                <MenuItem key={option.id} value={option.id}>
                  {option.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            options={filteredLetters}
            groupBy={option => option.serviceTitle}
            getOptionLabel={option => option.name}
            getOptionSelected={(option, value) => String(option.rootTemplateId) === String(value.rootTemplateId)}
            renderOption={option => (option.isActive ? option.name : `${option.name} (no active version)`)}
            value={selectedLetter}
            onChange={(event, option) => onLetterChange(option || null)}
            disabled={isLoading}
            renderInput={params => (
              <TextField
                {...params}
                label="Letter"
                required
                error={!!error}
                helperText={error || 'Type to search letters from Admin > Templates'}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Only letters sent on or after"
            type="date"
            fullWidth
            value={cutoffDate || ''}
            onChange={(e) => onCutoffChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={isLoading}
          />
          <FormHelperText>
            Defaults to today so letters sent before the rule existed do not create tasks. Pick an earlier date to
            include them, or clear it to include every letter ever sent.
          </FormHelperText>
        </Grid>
      </Grid>
    </Paper>
  );
};

LetterTriggerSection.propTypes = {
  letterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      rootTemplateId: PropTypes.number,
      name: PropTypes.string,
      serviceId: PropTypes.string,
      regardingId: PropTypes.string,
      isActive: PropTypes.bool,
    }),
  ),
  serviceEnum: PropTypes.array,
  regardingEnum: PropTypes.array,
  selectedRootTemplateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  cutoffDate: PropTypes.string,
  onLetterChange: PropTypes.func.isRequired,
  onCutoffChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
};

LetterTriggerSection.defaultProps = {
  letterOptions: [],
  serviceEnum: [],
  regardingEnum: [],
  selectedRootTemplateId: null,
  cutoffDate: '',
  error: null,
  isLoading: false,
};

export default LetterTriggerSection;
